import { useCallback } from 'react';

import { NUMBER_OF_PLAYERS, NUMBER_OF_PREV_PLAYERS } from 'constants/params';
import { StoryBoardTracks } from 'services/storyboard.service';
import { RootState } from 'store/store';
import { useAppStore } from 'store/store-hooks';
import { isNull, selectClipData } from 'components/app/_helpers/_app.helper';
import { selectLinkedGroup } from 'components/app/editor/_mediator/editor-clips/editor-clips-linking.helper';
import { EMPTY_CELL } from 'components/app/editor/_mediator/editor.slice';
import { PlaybackElement } from 'components/app/storyboard/sb-player/_mediator/sb-player.slice';
import { useSbPlayerContext } from './sb-player.provider';
import { isLinkedGroup } from './sb-player.helper';

export const useSbPlayerPlayback = () => {
  const { activePlayerIndex } = useSbPlayerContext();
  const redux = useAppStore();

  return useCallback((cursorCell: Index, selectedTrack: StoryBoardTracks) => {
    const data = selectData(redux.getState());
    const trackClips = data.storyBoard[selectedTrack];
    const { playbackClips, linkIndex } = getPlaybackClips(cursorCell, trackClips, data);
    const index = getActivePlayerIndex(cursorCell, trackClips, data);
    const clips = sortPlaybackClips(playbackClips, index);
    activePlayerIndex.current = index;

    return {
      activePlayerIndex: index,
      linkedGroupIndex: linkIndex,
      isEmptyCell: trackClips[cursorCell] === EMPTY_CELL,
      playbackClips: clips,
    };
  }, []);
};

/* S E L E C T O R */

type Data = ReturnType<typeof selectData>;

const selectData = (state: RootState) => {
  const { playbackClips, activePlayerIndex, isEmptyCell } = state.sb__player;
  const { storyBoard, clipsMap, linkedClips, markers } = state.editor;

  return { markers, playbackClips, activePlayerIndex, isEmptyCell, storyBoard, clipsMap, linkedClips };
};

/* H E L P E R S */

const getPlaybackElement = (
  trackIndex: number,
  trackClips: CellClip[],
  data: Data,
  getLinkedGroupIndex?: boolean
): { playbackElement: PlaybackElement; linkedGroupIndex: Index | null; linkedGroupLen: number } => {
  const result = { playbackElement: null, linkedGroupIndex: 0, linkedGroupLen: 0 };
  const clipId = trackClips[trackIndex];
  if (clipId && clipId !== EMPTY_CELL) {
    if (!data.clipsMap) return result;
    const linkedGroupId = selectClipData(clipId, data.clipsMap).clipGroupId;
    if (linkedGroupId) {
      const linkedGroup = selectLinkedGroup(linkedGroupId, data.linkedClips);
      const index = getLinkedGroupIndex ? linkedGroup.findIndex((el) => el === clipId) : null;
      const len = linkedGroup.length;
      return { playbackElement: linkedGroup, linkedGroupIndex: index, linkedGroupLen: len };
    }
    return { ...result, playbackElement: clipId };
  }
  return result;
};

const getPrevClips = (cursorCell: Index, trackClips: CellClip[], maxNumberOfPlayers: number, data: Data) => {
  const result: PlaybackElement[] = [];
  let trackIndex = cursorCell;

  while (trackIndex >= 0 && result.length < maxNumberOfPlayers) {
    const { playbackElement, linkedGroupLen } = getPlaybackElement(trackIndex, trackClips, data);
    if (!isNull(playbackElement)) result.push(playbackElement);
    trackIndex = isLinkedGroup(playbackElement) ? trackIndex - linkedGroupLen - 1 : trackIndex - 1;
  }
  return Array.from(Array(maxNumberOfPlayers))
    .map((_, index) => (result[index] ? result[index] : null))
    .reverse();
};

const getCurrentClip = (cursorCell: Index, trackClips: CellClip[], data: Data) => {
  const { playbackElement, linkedGroupLen, linkedGroupIndex } = getPlaybackElement(cursorCell, trackClips, data, true);
  const currentClip: PlaybackElement[] = [];
  let trackIndexPrev = cursorCell;
  let trackIndexNext = cursorCell;
  let linkIndex = linkedGroupIndex;
  if (!isNull(playbackElement)) {
    currentClip.push(playbackElement);
    if (isLinkedGroup(playbackElement)) {
      trackIndexPrev = cursorCell - linkedGroupIndex! - 1;
      trackIndexNext = cursorCell + linkedGroupLen - linkedGroupIndex!;
    } else {
      trackIndexPrev--;
      trackIndexNext++;
      linkIndex = null;
    }
  } else linkIndex = null;

  return { currentClip, trackIndexPrev, trackIndexNext, linkIndex };
};

const getNextClips = (cursorCell: Index, trackClips: CellClip[], maxNumberOfPlayers: number, data: Data) => {
  const result: PlaybackElement[] = [];
  let trackIndex = cursorCell;
  while (trackIndex <= trackClips.length && result.length < maxNumberOfPlayers) {
    const { playbackElement, linkedGroupLen } = getPlaybackElement(trackIndex, trackClips, data);
    if (!isNull(playbackElement)) result.push(playbackElement);
    trackIndex = isLinkedGroup(playbackElement) ? trackIndex + linkedGroupLen : trackIndex + 1;
  }
  return Array.from(Array(maxNumberOfPlayers)).map((_, index) => (result[index] ? result[index] : null));
};

const getPlaybackClips = (cursorCell: Index, trackClips: CellClip[], data: Data) => {
  const { currentClip, trackIndexPrev, trackIndexNext, linkIndex } = getCurrentClip(cursorCell, trackClips, data);
  const numberOfNextPlayers = NUMBER_OF_PLAYERS - NUMBER_OF_PREV_PLAYERS;
  const prevClips = getPrevClips(trackIndexPrev, trackClips, NUMBER_OF_PREV_PLAYERS, data);
  const nextClips = getNextClips(
    trackIndexNext,
    trackClips,
    currentClip.length ? numberOfNextPlayers - 1 : numberOfNextPlayers,
    data
  );
  return { playbackClips: [...prevClips, ...currentClip, ...nextClips], linkIndex };
};

const getActivePlayerIndex = (cursorCell: Index, trackClips: CellClip[], data: Data) => {
  const currentClip = trackClips.slice(cursorCell).find((el) => el !== EMPTY_CELL);
  if (currentClip && data.clipsMap) {
    const index = data.playbackClips.findIndex((el) => {
      if (isLinkedGroup(el)) return (el as string[]).findIndex((id) => id === currentClip) !== -1;
      return el === currentClip;
    });
    return index === -1 ? NUMBER_OF_PREV_PLAYERS : index;
  }
  if (data.isEmptyCell) return data.activePlayerIndex;
  else return data.activePlayerIndex < NUMBER_OF_PLAYERS - 1 ? data.activePlayerIndex + 1 : 0;
};

const sortPlaybackClips = (clips: PlaybackElement[], activePlayerIndex: number) => {
  const result = Array.from(Array(NUMBER_OF_PLAYERS));
  clips.forEach((clip, index) => {
    const newIndex = activePlayerIndex + index - NUMBER_OF_PREV_PLAYERS;
    const diff = NUMBER_OF_PLAYERS - newIndex;
    if (diff > 0) {
      if (newIndex < 0) result[NUMBER_OF_PLAYERS - Math.abs(newIndex)] = clip;
      else result[newIndex] = clip;
    } else result[Math.abs(diff)] = clip;
  });
  return result;
};
